/**
 * 账户表单 / 删除的**错误文案映射** —— 两个页面共用的唯一一份（批次 `94`）
 *
 * ## 为什么要独立成文件
 *
 * 账户的增删改现在有两个落点：
 * - **资产页**（`pages/AccountsPage.tsx`）：日常账户 / 社保账户；
 * - **债权债务页**（`pages/DebtsPage.tsx`）：往来账户 + 预付 / 待摊。
 *
 * 两处都要把"服务端返回的 409 该怎么解释"说成同一句话。若各写一份，
 * 同一个错误在两个页面会给出**不同的下一步建议**（一个说"改成收起"，一个说"再点一次"），
 * 用户会以为系统不稳定。⇒ 文案与判定都收在这一个文件里（铁律 24 的同型问题）。
 *
 * 🔴 本文件**只解释错误**，不做任何请求 —— 两个页面各自持有自己的 mutation。
 */

import type { ApiError } from '../api/http.js';
import { ApiError as ApiErrorClass } from '../api/http.js';

/**
 * 把异常转成**给人看的逐条消息**。
 *
 * 🔴 优先 `details.issues`（结构化清单），其次 `message`（中文原文，**原样展示、不重写**）——
 * 不去 `message` 里按 `；` 拆：拆字符串等于把**服务端的排版细节**当成接口。
 */
export function apiMessages(e: unknown): readonly string[] {
  if (!(e instanceof ApiErrorClass)) return [String(e)];
  const details = (e as ApiError).body.details as { issues?: unknown } | undefined;
  const issues = Array.isArray(details?.issues)
    ? details.issues.filter((x): x is string => typeof x === 'string')
    : [];
  if (issues.length > 0) return issues;
  return [(e as ApiError).message];
}

/**
 * 删除失败时，除原文外**额外**要说的那句话（`null` = 不需要额外说）。
 *
 * 🔴 两类 `409` 的处置方式**完全不同**，所以必须分开：
 * - **被引用** ⇒ 无解（删不掉）⇒ 指条明路「改为隐藏」（`AC-B6.5` 的替代路径）；
 * - **余额非零** ⇒ 有解（再点一次确认）⇒ 提示"确认后会清零"，且**要能说清清掉多少**。
 *   金额与币种由服务端 `details` 下发 ⇒ 端侧**不自己算**（也就不会算错）。
 */
export function deleteAdvice(e: unknown): string | null {
  if (!(e instanceof ApiErrorClass) || e.status !== 409) return null;
  const details = (e as ApiError).body.details as
    | { requiresConfirmation?: unknown; balance?: unknown; currency?: unknown }
    | undefined;
  if (details?.requiresConfirmation === true) return null; // 余额非零 ⇒ 走确认流，文案在按钮区
  return '若只是不想让它出现在净资产里，请改用「编辑 → 收起」—— 隐藏不删数据';
}
